export type UserRoleGates = {
  roles: string[];
  isAdmin: boolean;
  isAdminOrBuilder: boolean;
  /**
   * Admin, Builder or ReadOnly — the roles that belong to the tenant proper.
   * Mirrors TENANT_MEMBER_ROLES on the backend. Deliberately a positive check:
   * an unknown or not-yet-loaded role gets the narrow view rather than a flash
   * of the wide one.
   */
  canViewDataManagement: boolean;
  /**
   * An external dashboard viewer and nothing more. False for a user who also
   * holds a higher role, and false when there is no user yet — anything hiding
   * UI should test `canViewDataManagement` instead, which fails closed.
   */
  isReadOnlyPartnerOnly: boolean;
};

/**
 * Normalize whatever shape the roles arrived in, then answer the gates.
 *
 * Roles come through as a bare string or as an object keyed `key` or `name`
 * depending on the auth path, so every caller has to normalize before comparing.
 * Kept free of any auth import so it stays a plain function to test.
 *
 * Higher roles always win, matching ROLE_PRIORITY in src/models/context.py: a
 * user holding both ReadOnly_Partner and Admin is an Admin here.
 */
export function deriveUserRoleGates(rawRoles: unknown): UserRoleGates {
  const roles = (Array.isArray(rawRoles) ? rawRoles : []).map((r: any) =>
    (typeof r === 'string' ? r : r?.key || r?.name || '').toLowerCase(),
  );

  const has = (...keys: string[]) => roles.some((r) => keys.includes(r));

  return {
    roles,
    isAdmin: has('admin'),
    isAdminOrBuilder: has('admin', 'builder'),
    canViewDataManagement: has('admin', 'builder', 'readonly'),
    isReadOnlyPartnerOnly:
      roles.length > 0 && roles.every((r) => r === 'readonly_partner'),
  };
}
